import { Component, Input, inject, signal, OnDestroy, ElementRef, ViewChild, ChangeDetectorRef, NgZone } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { CardService } from '../../services/api/card.service';
import { MatDialog } from '@angular/material/dialog';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, Observable, startWith, map, firstValueFrom, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatInputModule } from '@angular/material/input';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { TagConfirmationDialogComponent } from './tag-confirmation-dialog.component';

/**
 * Tag Manager Component
 * 
 * A form control for managing an array of string tags using Angular Material chips
 */
@Component({
  selector: 'app-tag-manager',
  template: `
    <mat-form-field class="tag-manager" [class.disabled]="disabled" appearance="outline">
      <mat-label>{{ label }}</mat-label>
      <mat-chip-grid #chipGrid>
        <mat-chip-row *ngFor="let tag of tags()"
          [removable]="!disabled"
          (removed)="onChipRemove(tag)">
          {{ tag }}
          <button matChipRemove *ngIf="!disabled">
            <mat-icon>cancel</mat-icon>
          </button>
        </mat-chip-row>
        <input matInput
          #tagInput
          [formControl]="tagCtrl"
          [matChipInputFor]="chipGrid"
          [matAutocomplete]="auto"
          (keydown.enter)="handleEnterKey($event)"
          [placeholder]="'Type to search or create new tag'"
          [disabled]="disabled">
      </mat-chip-grid>
      <button 
        *ngIf="!disabled && tagCtrl.value?.trim()"
        matSuffix
        mat-icon-button
        color="primary"
        aria-label="Add tag"
        (click)="addNewTag()">
        <mat-icon>add</mat-icon>
      </button>
      <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selected($event)" (opened)="autocompleteOpened()" (closed)="autocompleteClosed()">
        <mat-option *ngFor="let tag of filteredTags | async" [value]="tag">
          {{tag}}
        </mat-option>
      </mat-autocomplete>
    </mat-form-field>
  `,
  styles: [`
    .tag-manager {
      width: 100%;
    }
    :host ::ng-deep .mdc-text-field--outlined.mdc-text-field--label-floating .mdc-floating-label {
      background-color: var(--mat-form-field-background);
    }
    :host ::ng-deep .mat-mdc-form-field-icon-suffix {
      display: flex;
      align-items: center;
    }
    button[matSuffix] {
      margin-right: 4px;
    }
  `],
  standalone: true,
  imports: [
    CommonModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatAutocompleteModule,
    ReactiveFormsModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TagManagerComponent,
      multi: true
    }
  ]
})
export class TagManagerComponent implements ControlValueAccessor, OnDestroy {
  @Input() label: string = 'Tags';
  @Input() disabled: boolean = false;
  @Input() defaultTags: string[] = [];
  @Input() set tagKey(value: string) {
    this._tagKey = value;
    if (value) {
      this.loadSavedTags();
    }
  }

  @ViewChild('tagInput') tagInput!: ElementRef<HTMLInputElement>;
  @ViewChild('chipGrid') chipGrid: any;
  @ViewChild('tagDropdown') tagDropdown: any;
  @ViewChild('auto') matAutocomplete: any;

  readonly separatorKeysCodes = [] as const;
  private _tagKey: string = '';
  tags = signal<string[]>([]);
  allTags: string[] = [];
  private destroy$ = new Subject<void>();
  private isAutocompleteOpen = false;

  tagCtrl = new FormControl('');
  filteredTags: Observable<string[]>;
  private currentFilteredTags: string[] = [];

  private cardService = inject(CardService);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  constructor() {
    this.filteredTags = this.tagCtrl.valueChanges.pipe(
      startWith(null),
      map((tag: string | null) => {
        if (!tag) return this.allTags.filter(t => !this.tags().includes(t));
        const filtered = this.allTags
          .filter(t => !this.tags().includes(t) && t.toLowerCase().includes(tag.toLowerCase()));
        this.currentFilteredTags = filtered;
        return filtered;
      })
    );
  }

  // ControlValueAccessor implementation
  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string[]): void {
    if (value) {
      this.tags.set(value);
    } else {
      this.tags.set([]);
    }
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private loadSavedTags(): void {
    if (this._tagKey) {
      this.cardService.getTags(this._tagKey)
        .pipe(takeUntil(this.destroy$))
        .subscribe(tags => {
          const merged = [...new Set([...this.defaultTags, ...tags])];
          this.allTags = merged;
        });
    } else if (this.defaultTags.length) {
      this.allTags = [...this.defaultTags];
    }
  }

  addTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    
    // Skip empty values
    if (!value) return;
    
    // Check if there's a matching option in the dropdown
    const bestMatch = this.findBestMatch(value);
    
    // If we have a best match, use it, otherwise create a new tag
    if (bestMatch) {
      this.addTagToList(bestMatch);
    } else {
      this.addTagToList(value);
    }
    
    // Clear the input value
    event.chipInput!.clear();
    this.tagCtrl.setValue(null);
  }

  /**
   * Handles the Enter key press to select the first matching option if available
   * Only works if the autocomplete dropdown is open
   */
  handleEnterKey(event: Event): void {
    // Only do something if the autocomplete is open
    if (!this.isAutocompleteOpen) return;
    
    const inputValue = this.tagCtrl.value?.trim();
    if (!inputValue) return;
    
    // Find the best matching tag
    const bestMatch = this.findBestMatch(inputValue);
    
    // If we have a matching option, prevent default action and select it
    if (bestMatch) {
      event.preventDefault();
      this.addTagToList(bestMatch);
      this.tagInput.nativeElement.value = '';
      this.tagCtrl.setValue(null);
    }
  }

  /**
   * Finds the best matching tag from the filtered options
   */
  private findBestMatch(input: string): string | null {
    if (!input || this.currentFilteredTags.length === 0) return null;
    
    // First look for exact match (case insensitive)
    const exactMatch = this.currentFilteredTags.find(
      tag => tag.toLowerCase() === input.toLowerCase()
    );
    if (exactMatch) return exactMatch;
    
    // Then look for a tag that starts with the input
    const startsWithMatch = this.currentFilteredTags.find(
      tag => tag.toLowerCase().startsWith(input.toLowerCase())
    );
    if (startsWithMatch) return startsWithMatch;
    
    // If only one option in the filtered list, use that
    if (this.currentFilteredTags.length === 1) {
      return this.currentFilteredTags[0];
    }
    
    // No good match found
    return null;
  }

  /**
   * Checks if there are any matching options for the given input
   */
  private hasMatchingOption(input: string): boolean {
    return this.findBestMatch(input) !== null;
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.addTagToList(event.option.viewValue);
    this.tagInput.nativeElement.value = '';
    this.tagCtrl.setValue(null);
  }

  private addTagToList(value: string): void {
    const currentTags = this.tags();
    if (!currentTags.includes(value)) {
      const newTags = [...currentTags, value];
      this.tags.set(newTags);
      this.onChange(newTags);
      this.onTouched();
      
      // Save to shared tags if not exists
      if (this._tagKey && !this.allTags.includes(value)) {
        this.cardService.saveTags(this._tagKey, [...this.allTags, value]);
        this.allTags = [...this.allTags, value];
      }
    }
  }

  onChipRemove(tag: string): void {
    if (this.disabled) return;

    // Ensure we're outside Angular's zone when handling focus changes
    this.ngZone.runOutsideAngular(() => {
      // First, blur any focused elements to avoid focus-related change detection issues
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // Use requestAnimationFrame to ensure DOM updates are complete
      requestAnimationFrame(() => {
        this.ngZone.run(() => {
          this.confirmRemove(tag);
        });
      });
    });
  }

  async confirmRemove(tag: string): Promise<void> {
    const dialogRef = this.dialog.open(TagConfirmationDialogComponent, {
      data: {
        message: `Do you want to remove "${tag}" from just this item, or delete it completely from the database?`,
        tag
      },
      autoFocus: false,
      restoreFocus: false,
      width: 'auto',
      maxWidth: '450px',
      panelClass: 'tag-confirmation-dialog'
    });

    try {
      const result = await firstValueFrom(dialogRef.afterClosed());
      
      // Run the removal logic in the next tick to avoid change detection issues
      setTimeout(async () => {
        if (result === 'remove') {
          this.removeFromItem(tag);
        } else if (result === 'delete') {
          // First remove from the current item
          this.removeFromItem(tag);
          // Then remove from the database
          await this.cardService.deleteTag(this._tagKey, tag);
          // Remove from allTags
          this.allTags = this.allTags.filter(t => t !== tag);
          // Remove from the dropdown's items list
          if (this.tagDropdown) {
            this.tagDropdown.removeFromDatabase(tag);
          }
        }
      });
    } catch (error) {
      console.error('Error in tag removal:', error);
    }
  }

  private removeFromItem(tag: string): void {
    const currentTags = this.tags().filter(t => t !== tag);
    this.tags.set(currentTags);
    this.onChange(currentTags);
    this.onTouched();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Tracks when the autocomplete dropdown is opened
   */
  autocompleteOpened(): void {
    this.isAutocompleteOpen = true;
  }

  /**
   * Tracks when the autocomplete dropdown is closed
   */
  autocompleteClosed(): void {
    this.isAutocompleteOpen = false;
  }

  /**
   * Adds a new tag when the plus button is clicked
   */
  addNewTag(): void {
    const value = this.tagCtrl.value?.trim();
    if (!value) return;
    
    this.addTagToList(value);
    this.tagInput.nativeElement.value = '';
    this.tagCtrl.setValue(null);
  }
} 